import { formatNumber } from '../../lib/format';

export interface StatusRow {
  label: string;
  value: number;
  /** The status dot beside the label — colour plus name, never colour alone. */
  dotColor: string;
}

/**
 * Counts across named states.
 *
 * One hue for every bar: the bars encode magnitude, and the state is
 * already carried by its own label and status dot. Painting each bar a
 * different colour would be decoration standing in for information — and
 * it is how the amber/green pair ended up indistinguishable to a
 * deuteranope when this was first drawn.
 */
export function StatusBars({ rows }: { rows: StatusRow[] }) {
  const max = Math.max(...rows.map((r) => r.value), 1);

  return (
    <ul className="space-y-2.5">
      {rows.map((row) => (
        <li key={row.label} className="group grid grid-cols-[7.5rem_1fr_3.5rem] items-center gap-3">
          <span className="flex items-center gap-1.5 text-xs text-ink-2">
            <span
              aria-hidden="true"
              className="size-2 shrink-0 rounded-full"
              style={{ backgroundColor: row.dotColor }}
            />
            {row.label}
          </span>

          <span className="relative block h-2.5 rounded-full bg-surface-2">
            <span
              className="absolute inset-y-0 left-0 rounded-full bg-accent transition-[width] duration-500"
              style={{ width: `${Math.max((row.value / max) * 100, row.value > 0 ? 2 : 0)}%` }}
            />
          </span>

          <span className="tabular text-right text-xs font-medium text-ink">
            {formatNumber(row.value)}
          </span>
        </li>
      ))}
    </ul>
  );
}
